import { FileManipulationKey, VisualFileType } from 'constants/visual'
import { useCallback, useMemo, useState } from 'react'

import { ActionTypeAPIData } from 'constants/redux'
import { CONST_PRODUCT_SERIAL } from 'constants/product'
import { RootStore } from 'models/redux'
import { SelfUploadProductCountryCodeMap } from '../../../Gallery/_main/contexts'
import { SkyReplacementOption } from 'models/purchaseFlow'
import constate from 'constate'
import { useClientProperty } from '../_main/contexts'
import { useDebouncedMemo } from 'utils/helpers'
import { useSelector } from 'react-redux'
import { useUserData } from 'components/contexts/UserDataContext'

/** Enum of all upload visual types */
export enum UploadVisualsTypes {
  AI_EDITING = 'AI_EDITING',
  ORIGINAL_STATE = 'ORIGINAL_STATE',
}

export const [ClientPropertySelfUploadPopupContext, useClientPropertySelfUploadPopup] = constate(({ assignmentId }: { assignmentId: string }) => {
  const { baseUserData } = useUserData()
  const { sessionId } = useClientProperty()

  const [uploadVisualsType, setUploadVisualsType] = useState<UploadVisualsTypes>(UploadVisualsTypes.ORIGINAL_STATE)
  const [isSkyReplacementEnabled, setIsSkyReplacementEnabled] = useState(false)
  const [selectedSkyReplacement, setSelectedSkyReplacement] = useState<SkyReplacementOption | null>(null)

  const productId = useMemo(() => {
    if (!baseUserData?.country) return 0

    return SelfUploadProductCountryCodeMap[baseUserData.country] || 0
  }, [baseUserData?.country])

  const sortFunction = useCallback((aKey: string, bKey: string) => {
    if (aKey > bKey) return 1
    else if (aKey < bKey) return -1
    else return 0
  }, [])

  const sortEntriesFunction = useCallback(([aKey, aObject]: [string, any], [bKey, bObject]: [string, any]) => {
    return sortFunction(aKey, bKey)
  }, [sortFunction])

  const loadPurchaseSessionVisuals = useSelector((state: RootStore) => state.APIData[ActionTypeAPIData.LOAD_PURCHASE_SESSION_VISUAL]?.[sessionId ?? ''])
  const listedPurchaseSessionVisuals = useSelector((state: RootStore) => state.APIData[ActionTypeAPIData.LIST_PURCHASE_SESSION_VISUALS]?.[sessionId ?? ''])

  const downloadProductVisuals = useMemo(() => loadPurchaseSessionVisuals?.[productId]?.[CONST_PRODUCT_SERIAL]?.[FileManipulationKey.DOWNLOAD], [loadPurchaseSessionVisuals, productId])
  const downloadVisualsEntries = useMemo(() => (downloadProductVisuals ? Object.entries(downloadProductVisuals).filter(([key, allTypes]) => allTypes?.[VisualFileType.RAW_THUMB]).sort(sortEntriesFunction) : []), [downloadProductVisuals, sortEntriesFunction])
  const downloadVisualsKeys = useMemo(() => (downloadVisualsEntries.filter(([key, allTypes]) => allTypes?.[VisualFileType.RAW_THUMB]).map(([key, allTypes]) => key)), [downloadVisualsEntries])

  const listedProductVisuals = useMemo(() => listedPurchaseSessionVisuals?.data?.visuals?.[productId], [listedPurchaseSessionVisuals, productId])
  const listedVisualKeys = useMemo(() => (listedProductVisuals ?? []).sort(sortFunction), [listedProductVisuals, sortFunction])

  const uploadProductVisuals = useMemo(() => loadPurchaseSessionVisuals?.[productId]?.[CONST_PRODUCT_SERIAL]?.[FileManipulationKey.UPLOAD], [loadPurchaseSessionVisuals, productId])
  const uploadVisualsEntries = useMemo(() => (uploadProductVisuals ? Object.entries(uploadProductVisuals).filter(([key, allTypes]) => allTypes?.[VisualFileType.RAW]).sort(sortEntriesFunction) : []), [uploadProductVisuals, sortEntriesFunction])
  const uploadVisualsKeys = useMemo(() => (uploadVisualsEntries.filter(([key, allTypes]) => allTypes?.[VisualFileType.RAW]).map(([key, allTypes]) => key)), [uploadVisualsEntries])

  const visualsKeys = useMemo(() => [...uploadVisualsKeys, ...downloadVisualsKeys].sort(sortFunction), [uploadVisualsKeys, downloadVisualsKeys, sortFunction])
  const visualsCount = useDebouncedMemo(() => visualsKeys.length || 0, [visualsKeys], 100)

  return {
    productId,
    uploadVisualsType,
    setUploadVisualsType,
    isSkyReplacementEnabled,
    selectedSkyReplacement,
    setSelectedSkyReplacement,
    setIsSkyReplacementEnabled,
    listedVisualKeys,
    visualsCount,
    downloadVisualsEntries,
    uploadVisualsEntries,
    sortEntriesFunction
  }
})
